<template>
  <div>
    <v-expansion-panels flat class="requisites mb-6">
      <v-expansion-panel>
        <v-expansion-panel-header class="requisites__header">Реквизиты</v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-for="row in requisites" :key="row.label" class="overview-row">
            <div class="overview-row__label">{{ row.label }}</div>
            <div class="overview-row__value">{{ row.value || '—' }}</div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <ClientBalances
      ref="balances"
      :corp-id="corpId"
      :company="client.company"
      :wallet="wallet"
      :loading="walletLoading"
      :error="walletError"
      :period="period"
      :custom-range="customRange"
      :wallet-period="walletPeriod"
      :is-admin="isAdmin"
      :can-accrue="canAccrue"
      :can-distribute="canDistribute"
      :can-buy="canBuy"
      @update:period="$emit('update:period', $event)"
      @update:custom-range="$emit('update:customRange', $event)"
      @reload="$emit('reload')"
    />

    <AvailableServices v-if="canBuy" :wallet="wallet" />

    <ClientAccruals :corp-id="corpId" :can-open-operations="canOpenOperations" @open-tab="$emit('open-tab', $event)" />

    <div class="overview-block mt-6">
      <div class="d-flex align-center">
        <h3 class="overview-block__title">Менеджеры</h3>
        <v-spacer />
        <a v-if="managers.length" class="text-primary caption mr-3" @click="$emit('open-tab', 'managers')">Все менеджеры →</a>
        <v-btn v-if="canAddManager" x-small color="primary" @click="$emit('add-manager')">+ Добавить</v-btn>
      </div>
      <div v-if="!managers.length" class="caption grey--text">
        Менеджеров пока нет. Они нужны, если тесты распределяются по городам, филиалам или сотрудникам. Без менеджеров компания отправляет тесты сама из буфера.
      </div>
      <div v-for="m in topManagers" :key="m.id" class="overview-row">
        <div class="overview-row__label">{{ m.name }}</div>
        <div class="overview-row__value">{{ m.balance }} шт.</div>
      </div>
    </div>

    <div class="overview-block mt-4">
      <div class="d-flex align-center">
        <h3 class="overview-block__title">Последние кандидаты</h3>
        <v-spacer />
        <a class="text-primary caption" @click="$emit('open-tab', 'candidates')">Все кандидаты →</a>
      </div>
      <ClientCandidates :corp-id="corpId" :company="client.company" :managers="managers" :limit="5" />
    </div>
  </div>
</template>

<script>
import ClientBalances from './ClientBalances.vue';
import AvailableServices from './AvailableServices.vue';
import ClientCandidates from './ClientCandidates.vue';
import ClientAccruals from './ClientAccruals.vue';

// Короткий список менеджеров в «Обзоре» (ТЗ, Э-3).
const TOP_MANAGERS = 5;

export default {
  name: 'ClientOverview',
  components: {
    ClientBalances, ClientCandidates, AvailableServices, ClientAccruals,
  },
  props: {
    client: { type: Object, required: true },
    corpId: { type: [Number, String], required: true },
    wallet: { type: Object, default: null },
    walletLoading: { type: Boolean, default: false },
    walletError: { type: Object, default: null },
    period: { type: String, required: true },
    customRange: { type: Object, default: () => ({}) },
    walletPeriod: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
    canAccrue: { type: Boolean, default: false },
    canDistribute: { type: Boolean, default: false },
    canBuy: { type: Boolean, default: false },
    partnerName: { type: String, default: '' },
    referrerName: { type: String, default: '' },
    showPartner: { type: Boolean, default: false },
    canAddManager: { type: Boolean, default: false },
  },
  methods: {
    // Кнопки шапки карточки вызывают действия таблицы остатков
    exportXlsx() {
      if (this.$refs.balances) this.$refs.balances.exportXlsx();
    },
    openTransfer(mode, test) {
      if (this.$refs.balances) this.$refs.balances.openTransfer(mode, test);
    },
  },
  computed: {
    // Вкладка «История операций» открыта админу и тем, у кого есть право на аналитику
    canOpenOperations() {
      return this.isAdmin || this.$store.getters['auth/isAllowed']('bookmark_analytics');
    },
    managers() {
      return this.wallet ? this.wallet.managers : [];
    },
    topManagers() {
      return [...this.managers].sort((a, b) => b.balance - a.balance).slice(0, TOP_MANAGERS);
    },
    requisites() {
      const contact = [this.client.last_name, this.client.first_name].filter(Boolean).join(' ');
      return [
        { label: 'Компания', value: this.client.company },
        { label: 'ИНН', value: this.client.taxpayer_id_number },
        { label: 'Email', value: this.client.email },
        { label: 'Менеджер', value: contact },
        ...(this.showPartner ? [
          { label: 'Супервизор', value: this.partnerName },
          { label: 'Реферал', value: this.referrerName },
        ] : []),
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.requisites {
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  &__header {
    font-size: 16px;
    font-weight: 600;
  }
}

.overview-block {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  height: 100%;

  &__title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
}

.overview-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px solid #f2f2f2;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  &__label {
    color: #757575;
  }

  &__value {
    text-align: right;
    color: #212121;
  }
}
</style>
